import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/useToast';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MATCH_TYPE_LABELS = {
  contains: 'Contains',
  equals: 'Equals',
  starts_with: 'Starts With',
  ends_with: 'Ends With'
};

export default function ExclusionMatchesModal({ pattern, onClose }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { error: toastError } = useToast();

  const fetchMatches = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }

      const res = await fetch(`${API_URL}/campaigns/exclusion-patterns/${pattern.id}/matches?${params.toString()}`, {
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch matches');
      }

      const data = await res.json();
      setMatches(data);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pattern.id, toastError]);

  useEffect(() => {
    fetchMatches(search);
  }, [fetchMatches, search]);

  const handleExportCsv = () => {
    const url = `${API_URL}/campaigns/exclusion-patterns/${pattern.id}/matches?export=true`;
    window.open(url, '_blank');
  };

  const customerName = (c) => {
    return `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
  };

  const OptInBadge = ({ value }) => (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
      value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
    }`}>
      {value ? '✓ Yes' : '✗ No'}
    </span>
  );

  const matchTypeLabel = MATCH_TYPE_LABELS[pattern.match_type] || pattern.match_type;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Customers Matching Exclusion Pattern
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Pattern: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-indigo-600 font-mono">{pattern.pattern}</code>
              {' '}({matchTypeLabel})
              {pattern.description && <span className="text-gray-400 ml-2">— {pattern.description}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex-1 max-w-sm">
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {matches.length} customer{matches.length !== 1 ? 's' : ''} match{matches.length !== 1 ? 'es' : ''}
            </span>
            <button
              onClick={handleExportCsv}
              disabled={matches.length === 0}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {search ? 'No matches found for your search.' : 'No customers match this exclusion pattern.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Mobile Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Home Phone</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">SMS Opt-In</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Voice Opt-In</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((customer) => (
                    <tr key={customer.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {customerName(customer)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {customer.mobile_phone || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {customer.home_phone || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <OptInBadge value={customer.opt_in_text_messages} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <OptInBadge value={customer.opt_in_phone_messages} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
